import DOMPurify from "dompurify";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconQuestionMarkCircleFilled from "../../img/icons/question-mark-circle-filled.svg";
import { dynamicPlaceComponent } from "../utils/react";

const TOOLTIP_TAGS = ["sup", "sub", "strong", "em", "b", "i", "br", "div", "p"];

/**
 * Editors write sup/sub and line breaks (`div`/`p`) in the CMS, so tooltip
 * copy stays HTML, but no other tags and no attributes should reach the page.
 */
export const sanitizeTooltipHTML = (html: string): string =>
    DOMPurify.sanitize(html, {
        ALLOWED_TAGS: TOOLTIP_TAGS,
        ALLOWED_ATTR: [],
        ALLOW_DATA_ATTR: false,
        ALLOW_ARIA_ATTR: false,
    });

/**
 * Activate tooltips. HTML should be in the format:
 *
 *     <span data-tooltip="This is the content of my fancy new tooltip."></span>
 */
export const initTooltips = async (lazy = true) => {
    let index = 0;
    dynamicPlaceComponent(
        "[data-tooltip]:not([data-tooltip-rendered])",
        async (elem) => {
            const text = elem.dataset.tooltip;
            if (!text) {
                return null;
            }
            const srText = elem.dataset.tooltipSr || t`More information`;
            if (elem.dataset.tooltipRendered === "true") {
                return null;
            }
            elem.setAttribute("data-tooltip-rendered", "true");
            const { Tooltip } = await import("react-tooltip");
            const tipID = `tooltip-${index++}`;
            return (
                <>
                    <span
                        className="tooltip"
                        data-tooltip-id={tipID}
                        tabIndex={0}
                    >
                        <SVG
                            className="tooltip__handle"
                            src={iconQuestionMarkCircleFilled}
                            aria-hidden="true"
                        />
                        <span className="ada-screenreader-only">{srText}</span>
                    </span>
                    <Tooltip
                        id={tipID}
                        className="tooltip__content"
                        globalCloseEvents={{ escape: true, scroll: true }}
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: sanitizeTooltipHTML(text),
                            }}
                        />
                    </Tooltip>
                </>
            );
        },
        lazy,
    );
};
